import { type CSSProperties, type MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ArrowLeft, Pause, Play, Settings, SkipForward } from 'lucide-react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  getPublicChannel,
  resolveMediaUrl,
  type PublicChannelDetail,
  type PublicChannelVisualAsset
} from '../api'
import { getVisualDerivativeUrl } from '../api/visuals'
import { getChannelColorTheme } from '../channelColorThemes'
import { PublicVolumeControl } from '../components/PublicVolumeControl'
import { useChannelAudioPlayer } from '../features/publicPlayback/useChannelAudioPlayer'
import styles from './PublicChannelPage.module.css'

type VisualLayerKey = 'a' | 'b'
type ImageMotion = 'none' | 'pan' | 'zoom' | 'panzoom'

type VisualSettings = {
  fadeDuration: number
  imageDuration: number
  motion: ImageMotion
}

type VisualLayer = {
  asset: PublicChannelVisualAsset | null
  index: number
  playbackKey: number
}

const cx = (...classNames: Array<string | false | null | undefined>): string =>
  classNames
    .filter(Boolean)
    .map((className) => styles[className as string] ?? className)
    .join(' ')

const preloadImage = (url: string): Promise<void> =>
  new Promise((resolve, reject) => {
    const image = new Image()

    image.onload = () => resolve()
    image.onerror = () => reject(new Error('Image preload failed'))
    image.src = url
  })

const defaultSettings: VisualSettings = {
  fadeDuration: 2,
  imageDuration: 12,
  motion: 'pan'
}

const formatTime = (value: number): string => {
  if (!Number.isFinite(value) || value <= 0) {
    return '0:00'
  }

  const minutes = Math.floor(value / 60)
  const seconds = Math.floor(value % 60).toString().padStart(2, '0')

  return `${minutes}:${seconds}`
}

const getMotionClassName = (
  motion: ImageMotion,
  index: number
): string => {
  if (motion === 'none') {
    return ''
  }

  const variants: Record<Exclude<ImageMotion, 'none'>, string[]> = {
    pan: ['kb-pan-right', 'kb-pan-left', 'kb-pan-up'],
    zoom: ['kb-zoom-in', 'kb-zoom-out'],
    panzoom: ['kb-panzoom-a', 'kb-panzoom-b', 'kb-panzoom-c']
  }

  const pool = variants[motion]

  return pool[index % pool.length] ?? ''
}

const VisualAssetLayer = ({
  isActive,
  layer,
  onVideoEnded,
  settings
}: {
  isActive: boolean
  layer: VisualLayer
  onVideoEnded: () => void
  settings: VisualSettings
}) => {
  const asset = layer.asset
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const motionClassName = asset?.asset_type === 'image'
    ? getMotionClassName(settings.motion, layer.index)
    : ''

  useEffect(() => {
    const video = videoRef.current
    let pauseTimer: number | null = null

    if (!video) {
      return
    }

    if (!isActive) {
      pauseTimer = window.setTimeout(() => {
        video.pause()
        video.currentTime = 0
      }, settings.fadeDuration * 1000 + 120)

      return () => {
        if (pauseTimer) {
          window.clearTimeout(pauseTimer)
        }
      }
    }

    video.currentTime = 0
    void video.play().catch(() => {
      // Muted visual videos should autoplay; if the browser refuses, keep the frame visible.
    })
  }, [asset?.id, isActive, layer.playbackKey, settings.fadeDuration])

  return (
    <div
      className={cx('channel-visual-layer', isActive && 'active')}
      style={{ transitionDuration: `${settings.fadeDuration}s` }}
    >
      {asset?.asset_type === 'image' && (
        <img
          alt=""
          className={cx(motionClassName)}
          draggable={false}
          key={`${asset.id}-${layer.playbackKey}-${settings.motion}-${settings.imageDuration}`}
          src={resolveMediaUrl(getVisualDerivativeUrl(asset, 'player'))}
          style={{ animationDuration: `${settings.imageDuration + settings.fadeDuration}s` }}
        />
      )}
      {asset?.asset_type === 'video' && (
        <video
          key={`${asset.id}-${layer.playbackKey}`}
          muted
          onEnded={() => {
            if (isActive) {
              onVideoEnded()
            }
          }}
          playsInline
          preload="auto"
          ref={videoRef}
          src={resolveMediaUrl(asset.file_url)}
        />
      )}
    </div>
  )
}

export const PublicChannelPage = () => {
  const navigate = useNavigate()
  const { channelSlug = '' } = useParams()
  const player = useChannelAudioPlayer()
  const {
    currentTime,
    currentTrack,
    duration,
    errorMessage: playerErrorMessage,
    isPlaying,
    next,
    pause,
    playChannel,
    resume,
    seek,
    setVolume,
    stop
  } = player

  const [channel, setChannel] = useState<PublicChannelDetail | null>(null)
  const [loadState, setLoadState] = useState<'loading' | 'ready' | 'error'>('loading')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [activeLayer, setActiveLayer] = useState<VisualLayerKey>('a')
  const [layerA, setLayerA] = useState<VisualLayer>({ asset: null, index: 0, playbackKey: 0 })
  const [layerB, setLayerB] = useState<VisualLayer>({ asset: null, index: 0, playbackKey: 0 })
  const [currentAssetIndex, setCurrentAssetIndex] = useState(0)
  const [controlsVisible, setControlsVisible] = useState(true)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [settings, setSettings] = useState<VisualSettings>(defaultSettings)
  const [toast, setToast] = useState<string | null>(null)

  const visualTimerRef = useRef<number | null>(null)
  const hideControlsTimerRef = useRef<number | null>(null)
  const toastTimerRef = useRef<number | null>(null)
  const startedAudioRef = useRef(false)
  const visualPlaybackKeyRef = useRef(0)
  const transitioningRef = useRef(false)

  const assets = channel?.visual_assets ?? []
  const currentAsset = assets[currentAssetIndex] ?? null
  const progress = duration > 0
    ? Math.min(100, (currentTime / duration) * 100)
    : 0

  const trackLabel = useMemo(() => {
    if (!currentTrack) {
      return 'Loading track...'
    }

    return currentTrack.title
  }, [currentTrack])

  const artistLabel = currentTrack?.artist ?? channel?.name ?? ''
  const colorTheme = getChannelColorTheme(channel?.color_theme)

  const showToast = useCallback((message: string): void => {
    setToast(message)

    if (toastTimerRef.current) {
      window.clearTimeout(toastTimerRef.current)
    }

    toastTimerRef.current = window.setTimeout(() => setToast(null), 2800)
  }, [])

  const revealControls = useCallback((): void => {
    setControlsVisible(true)

    if (hideControlsTimerRef.current) {
      window.clearTimeout(hideControlsTimerRef.current)
    }

    hideControlsTimerRef.current = window.setTimeout(() => {
      if (!settingsOpen) {
        setControlsVisible(false)
      }
    }, 3800)
  }, [settingsOpen])

  const clearVisualTimer = useCallback((): void => {
    if (visualTimerRef.current) {
      window.clearTimeout(visualTimerRef.current)
      visualTimerRef.current = null
    }
  }, [])

  const advanceVisual = useCallback(async (): Promise<void> => {
    if (assets.length <= 1 || transitioningRef.current) {
      return
    }

    transitioningRef.current = true
    const nextIndex = (currentAssetIndex + 1) % assets.length
    const nextLayer: VisualLayerKey = activeLayer === 'a' ? 'b' : 'a'
    const nextAsset = assets[nextIndex]!
    const nextVisualLayer = {
      asset: nextAsset,
      index: nextIndex,
      playbackKey: visualPlaybackKeyRef.current + 1
    }
    visualPlaybackKeyRef.current = nextVisualLayer.playbackKey

    if (nextAsset.asset_type === 'image') {
      await preloadImage(resolveMediaUrl(getVisualDerivativeUrl(nextAsset, 'player'))).catch(() => undefined)
    }

    if (nextLayer === 'a') {
      setLayerA(nextVisualLayer)
    } else {
      setLayerB(nextVisualLayer)
    }

    window.requestAnimationFrame(() => {
      setActiveLayer(nextLayer)
      setCurrentAssetIndex(nextIndex)
      window.setTimeout(() => {
        transitioningRef.current = false
      }, settings.fadeDuration * 1000 + 100)
    })
  }, [activeLayer, assets, currentAssetIndex, settings.fadeDuration])

  const scheduleNextVisual = useCallback((): void => {
    clearVisualTimer()

    if (!currentAsset || assets.length <= 1) {
      return
    }

    const durationSeconds = currentAsset.asset_type === 'video'
      ? currentAsset.duration_seconds ?? settings.imageDuration
      : settings.imageDuration

    visualTimerRef.current = window.setTimeout(() => {
      void advanceVisual()
    }, Math.max(3, durationSeconds) * 1000)
  }, [advanceVisual, assets.length, clearVisualTimer, currentAsset, settings.imageDuration])

  const handleVideoEnded = useCallback((): void => {
    clearVisualTimer()
    void advanceVisual()
  }, [advanceVisual, clearVisualTimer])

  useEffect(() => {
    const loadChannel = async (): Promise<void> => {
      startedAudioRef.current = false
      stop()
      setLoadState('loading')
      setErrorMessage(null)

      try {
        const payload = await getPublicChannel(channelSlug)
        visualPlaybackKeyRef.current += 1
        setChannel(payload)
        setLayerA({
          asset: payload.visual_assets[0] ?? null,
          index: 0,
          playbackKey: visualPlaybackKeyRef.current
        })
        setLayerB({ asset: null, index: 0, playbackKey: 0 })
        setActiveLayer('a')
        setCurrentAssetIndex(0)
        setLoadState('ready')
      } catch (error) {
        setErrorMessage(error instanceof Error ? error.message : 'Unable to load channel')
        setLoadState('error')
      }
    }

    void loadChannel()
  }, [channelSlug, stop])

  useEffect(() => {
    if (!channel || startedAudioRef.current) {
      return
    }

    startedAudioRef.current = true
    void playChannel(channel)
  }, [channel, playChannel])

  useEffect(() => {
    scheduleNextVisual()

    return clearVisualTimer
  }, [clearVisualTimer, scheduleNextVisual])

  useEffect(() => {
    revealControls()
  }, [revealControls])

  useEffect(() => () => {
    clearVisualTimer()
    stop()

    if (hideControlsTimerRef.current) window.clearTimeout(hideControlsTimerRef.current)
    if (toastTimerRef.current) window.clearTimeout(toastTimerRef.current)
  }, [clearVisualTimer, stop])

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === ' ' || event.key.toLowerCase() === 'k') {
        event.preventDefault()
        if (isPlaying) {
          pause()
        } else {
          void resume()
        }
        revealControls()
      }

      if (event.key.toLowerCase() === 'n' || event.key === 'ArrowRight') {
        event.preventDefault()
        void next()
        revealControls()
      }

      if (event.key.toLowerCase() === 's') {
        event.preventDefault()
        setSettingsOpen((value) => !value)
        revealControls()
      }

      if (event.key === 'Escape') {
        event.preventDefault()
        if (settingsOpen) {
          setSettingsOpen(false)
          revealControls()
        } else {
          navigate('/channels')
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown)

    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isPlaying, navigate, next, pause, resume, revealControls, settingsOpen])

  const handleProgressClick = (event: MouseEvent<HTMLDivElement>): void => {
    if (duration <= 0) {
      return
    }

    const rect = event.currentTarget.getBoundingClientRect()
    const ratio = Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width))
    seek(ratio * duration)
    revealControls()
  }

  const handleBack = (): void => {
    showToast('Returning to channels...')
    navigate('/channels')
  }

  const handlePlayPause = (): void => {
    if (isPlaying) {
      pause()
    } else if (currentTrack) {
      void resume()
    } else if (channel) {
      void playChannel(channel)
    }

    revealControls()
  }

  return (
    <main
      className={cx('channel-view', controlsVisible && 'show-cursor')}
      style={{
        '--channel-accent': colorTheme.accent,
        '--channel-accent-hover': colorTheme.accentHover,
        '--channel-accent-glow': colorTheme.accentGlow
      } as CSSProperties}
      onClick={revealControls}
      onMouseMove={revealControls}
      onTouchStart={revealControls}
    >
      <section className={cx('channel-visual-stage')} aria-label="Channel visuals">
        <VisualAssetLayer
          isActive={activeLayer === 'a'}
          layer={layerA}
          onVideoEnded={handleVideoEnded}
          settings={settings}
        />
        <VisualAssetLayer
          isActive={activeLayer === 'b'}
          layer={layerB}
          onVideoEnded={handleVideoEnded}
          settings={settings}
        />
        <div className={cx('channel-vignette')} />
        <div className={cx('channel-grain')} />
        {channel && <div className={cx('channel-name')}>{channel.name}</div>}
      </section>

      <section className={cx('channel-controls-overlay', controlsVisible || settingsOpen ? 'visible' : 'dim')}>
        <button
          className={cx('channel-back-button')}
          onClick={(event) => {
            event.stopPropagation()
            handleBack()
          }}
          type="button"
          aria-label="Back to channels list"
          title="Back to channels"
        >
          <ArrowLeft size={20} />
        </button>

        <div className={cx('channel-kb-hint')}>
          Space: play/pause &middot; N: next track &middot; S: settings &middot; Esc: exit
        </div>

        <button
          className={cx('channel-settings-toggle', settingsOpen && 'active')}
          onClick={(event) => {
            event.stopPropagation()
            setSettingsOpen((value) => !value)
            revealControls()
          }}
          type="button"
          aria-label="Visual settings"
          title="Visual settings"
        >
          <Settings size={18} />
        </button>

        <aside className={cx('channel-audio-player')} aria-label="Audio player">
          <button
            className={cx('channel-audio-btn', 'channel-audio-play-btn')}
            onClick={(event) => {
              event.stopPropagation()
              handlePlayPause()
            }}
            type="button"
            aria-label={isPlaying ? 'Pause' : 'Play'}
          >
            {isPlaying ? <Pause size={18} fill="currentColor" /> : <Play size={18} fill="currentColor" />}
          </button>

          <div className={cx('channel-eq-container')} aria-hidden="true">
            {[0, 1, 2, 3].map((bar) => (
              <span
                className={cx('channel-eq-bar', isPlaying && 'playing')}
                key={bar}
              />
            ))}
          </div>

          <div className={cx('channel-track-copy')}>
            <strong>{trackLabel}</strong>
            <span>{artistLabel}</span>
          </div>

          <div className={cx('channel-time-display')}>
            {formatTime(currentTime)} / {formatTime(duration)}
          </div>

          <button
            className={cx('channel-audio-btn')}
            onClick={(event) => {
              event.stopPropagation()
              void next()
              revealControls()
            }}
            type="button"
            aria-label="Next track"
            title="Next track"
          >
            <SkipForward size={16} fill="currentColor" />
          </button>

          <PublicVolumeControl
            onChange={setVolume}
            onInteract={revealControls}
            tone="dark"
            volume={player.volume}
          />

          <div
            className={cx('channel-progress-track')}
            onClick={(event) => {
              event.stopPropagation()
              handleProgressClick(event)
            }}
            role="presentation"
          >
            <div className={cx('channel-progress-fill')} style={{ width: `${progress}%` }} />
          </div>
        </aside>
      </section>

      <aside
        className={cx('channel-visual-settings', settingsOpen && 'open')}
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-label="Visual playback settings"
      >
        <div className={cx('channel-setting-group')}>
          <div className={cx('channel-setting-label')}>Image Motion</div>
          <div className={cx('channel-setting-options')}>
            {(['none', 'pan', 'zoom', 'panzoom'] as ImageMotion[]).map((motion) => (
              <button
                className={cx('channel-setting-option', settings.motion === motion && 'active')}
                key={motion}
                onClick={() => setSettings((value) => ({ ...value, motion }))}
                type="button"
              >
                {motion === 'panzoom' ? 'Pan + Zoom' : motion.slice(0, 1).toUpperCase() + motion.slice(1)}
              </button>
            ))}
          </div>
        </div>

        <div className={cx('channel-setting-group')}>
          <label className={cx('channel-setting-label')} htmlFor="fade-duration">
            Transition Fade
          </label>
          <input
            className={cx('channel-setting-slider')}
            id="fade-duration"
            max={4}
            min={0.5}
            onChange={(event) => {
              setSettings((value) => ({ ...value, fadeDuration: Number(event.target.value) }))
            }}
            step={0.5}
            type="range"
            value={settings.fadeDuration}
          />
          <div className={cx('channel-slider-value')}>{settings.fadeDuration.toFixed(1)}s</div>
        </div>

        <div className={cx('channel-setting-group')}>
          <label className={cx('channel-setting-label')} htmlFor="image-duration">
            Image Display Duration
          </label>
          <input
            className={cx('channel-setting-slider')}
            id="image-duration"
            max={30}
            min={5}
            onChange={(event) => {
              setSettings((value) => ({ ...value, imageDuration: Number(event.target.value) }))
            }}
            step={1}
            type="range"
            value={settings.imageDuration}
          />
          <div className={cx('channel-slider-value')}>{settings.imageDuration}s</div>
        </div>
      </aside>

      {loadState === 'loading' && (
        <section className={cx('channel-loading-screen')} aria-live="polite">
          <div className={cx('channel-loader-ring')} />
          <div className={cx('channel-loader-text')}>LOADING</div>
        </section>
      )}

      {loadState === 'error' && (
        <section className={cx('channel-state-message')} aria-live="polite">
          <strong>{errorMessage}</strong>
          <button className={cx('channel-state-button')} onClick={handleBack} type="button">
            Back to channels
          </button>
        </section>
      )}

      {loadState === 'ready' && assets.length === 0 && (
        <section className={cx('channel-state-message')} aria-live="polite">
          <strong>No visuals are ready for this channel.</strong>
          <button className={cx('channel-state-button')} onClick={handleBack} type="button">
            Back to channels
          </button>
        </section>
      )}

      {playerErrorMessage && (
        <div className={cx('channel-toast', 'show')}>{playerErrorMessage}</div>
      )}
      {toast && <div className={cx('channel-toast', 'show')}>{toast}</div>}
    </main>
  )
}
